import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ssa`;
const Ctx = createContext(null);

export const PERSPECTIVES = [
  { key: "buyer", label: "Buyer", short: "Buyer" },
  { key: "procurement", label: "Infosys Procurement", short: "Procurement" },
  { key: "partner_a", label: "Partner A", short: "Partner A", pid: "A" },
  { key: "partner_b", label: "Partner B", short: "Partner B", pid: "B" },
  { key: "partner_c", label: "Partner C", short: "Partner C", pid: "C" },
  { key: "partner_d", label: "Partner D", short: "Partner D", pid: "D" },
  { key: "partner_e", label: "Partner E", short: "Partner E", pid: "E" },
  { key: "partner_f", label: "Partner F", short: "Partner F", pid: "F" },
  { key: "partner_g", label: "Partner G", short: "Partner G", pid: "G" },
  { key: "partner_h", label: "Partner H", short: "Partner H", pid: "H" },
];
export const CATEGORY_PARTNERS = { DM: ["A", "B", "C", "D"], FM: ["E", "F", "G", "H"] };
export const DEFAULT_SERVICE = { DM: "DM06", FM: "FM01" };
export const catOf = (sid) => (sid || "").slice(0, 2) === "FM" ? "FM" : "DM";

export const fmtMoney = (v, cur = "EUR") =>
  new Intl.NumberFormat("en-NL", { style: "currency", currency: cur || "EUR", minimumFractionDigits: 2 }).format(Number(v || 0));

export const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d?.errors) return d.errors.join(" · ");
  if (d?.message) return d.message;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(" · ");
  return e?.message || "Something went wrong";
};

export function SSAProvider({ children }) {
  const [config, setConfig] = useState(null);
  const [catalogs, setCatalogs] = useState({});
  const [category, setCategoryState] = useState(() => localStorage.getItem("ssa_category") || "DM");
  const [activeRid, setActiveRidState] = useState(() => localStorage.getItem("ssa_active_rid") || null);
  const [scenario, setScenario] = useState(() => localStorage.getItem("ssa_scenario") || "DM06");
  const [refreshKey, setRefreshKey] = useState(0);

  const call = useCallback(
    (method, path, data, perspective = "buyer") =>
      axios({ method, url: `${API}${path}`, data, withCredentials: true, headers: { "X-SSA-Perspective": perspective } }).then((r) => r.data),
    []
  );

  useEffect(() => {
    const warn = (what) => (e) => console.debug(`[SSA] ${what} failed`, e?.message);
    call("get", "/config").then(setConfig).catch(warn("config load"));
    ["DM", "FM"].forEach((c) => call("get", `/catalog?category=${c}`).then((d) => setCatalogs((m) => ({ ...m, [c]: d }))).catch(warn(`${c} catalog load`)));
  }, [call]);

  const setActiveRid = useCallback((rid) => {
    setActiveRidState(rid);
    if (rid) localStorage.setItem("ssa_active_rid", rid);
    else localStorage.removeItem("ssa_active_rid");
  }, []);

  const pickScenario = useCallback((s) => { setScenario(s); localStorage.setItem("ssa_scenario", s); }, []);
  const setCategory = useCallback((c) => {
    setCategoryState(c);
    localStorage.setItem("ssa_category", c);
    pickScenario(DEFAULT_SERVICE[c]);
  }, [pickScenario]);
  const bump = useCallback(() => setRefreshKey((k) => k + 1), []);

  const value = useMemo(
    () => ({
      config, catalogs, catalog: catalogs[category], category, call, activeRid, setActiveRid, scenario,
      setCategory, setScenario: pickScenario, refreshKey, bump,
    }),
    [config, catalogs, category, call, activeRid, setActiveRid, scenario, setCategory, pickScenario, refreshKey, bump]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useSSA = () => useContext(Ctx);

export function perspectiveFromPath(pathname) {
  if (pathname.startsWith("/services/workbench") || pathname.startsWith("/services/insights")) return "procurement";
  const m = pathname.match(/^\/services\/partner\/([A-H])/);
  if (m) return `partner_${m[1].toLowerCase()}`;
  return "buyer";
}

export function pathForPerspective(key, rid, category = "DM") {
  if (key === "procurement") return rid ? `/services/workbench/${rid}` : "/services/workbench";
  if (key.startsWith("partner_")) return `/services/partner/${key.slice(-1).toUpperCase()}`;
  return rid ? `/services/requests/${rid}` : `/services/c/${category}`;
}
